/**
 * Link-level scale — pure business core of the 5-level channel quality mapping.
 *
 * Framework-free, UI-free: emits only string level codes and numeric data.
 * Human-readable labels / localization belong to `ui/labels.ts`.
 */

/** Raw scoring output — includes "muted" (no data yet / not applicable). */
export type LinkLevel = "good" | "normal" | "degraded" | "bad" | "critical" | "unknown" | "muted";

/** What a snapshot actually stores: "muted" is always resolved to "unknown" before
 * anything is persisted (see `resolveMutedLevel`) — nothing downstream of a snapshot
 * (`ConnectionSnapshot.linkLevel`, `LinkQualityPayload.level`) ever sees "muted". */
export type ResolvedLinkLevel = Exclude<LinkLevel, "muted">;

/** Collapse "muted" to "unknown" — the one place both `connection-quality.ts` and
 * `link-quality.ts` used to do this same ternary independently. */
export function resolveMutedLevel(level: LinkLevel): ResolvedLinkLevel {
  return level === "muted" ? "unknown" : level;
}

export const HISTORY_LEN = 5;

const LEVEL_RANK: Record<LinkLevel, number> = {
  critical: 5,
  bad: 4,
  degraded: 3,
  normal: 2,
  good: 1,
  unknown: 0,
  muted: 0,
};

/** Alias warn → normal (legacy 3-level payload). */
export function normalizeLevel(level: string | null | undefined): LinkLevel {
  const key = String(level || "")
    .trim()
    .toLowerCase();
  if (key === "warn") return "normal";
  if (
    key === "good" ||
    key === "normal" ||
    key === "degraded" ||
    key === "bad" ||
    key === "critical"
  ) {
    return key;
  }
  return "muted";
}

export function worseLevel(a: string | null | undefined, b: string | null | undefined): LinkLevel {
  const na = normalizeLevel(a);
  const nb = normalizeLevel(b);
  return (LEVEL_RANK[na] || 0) >= (LEVEL_RANK[nb] || 0) ? na : nb;
}

export function rttToLevel(rttMs: number | null | undefined): LinkLevel {
  if (rttMs == null || !Number.isFinite(Number(rttMs))) return "muted";
  const v = Number(rttMs);
  if (v > 2000) return "critical";
  if (v > 800) return "bad";
  if (v > 250) return "degraded";
  if (v > 120) return "normal";
  return "good";
}

export function lossToLevel(lossPct: number | null | undefined): LinkLevel {
  if (lossPct == null || !Number.isFinite(Number(lossPct))) return "muted";
  const v = Number(lossPct);
  if (v > 20) return "critical";
  if (v > 10) return "bad";
  if (v > 5) return "degraded";
  if (v > 2) return "normal";
  return "good";
}

export function transportToLevel(state: string | null | undefined): LinkLevel {
  const s = String(state || "").toLowerCase();
  if (s === "failed" || s === "disconnected" || s === "closed") return "critical";
  if (s === "connected" || s === "completed") return "good";
  if (!s || s === "new") return "muted";
  return "degraded";
}

/** Inputs used to score a link-quality snapshot. */
export interface LinkQualityScoreInput {
  transportState?: string;
  lossPercentRecent?: number | null;
  rttMs?: number | null;
}

/** Итоговый Level = worst(transport, loss, rtt). */
export function scoreLinkLevel(s: LinkQualityScoreInput, hasIntervalData: boolean): LinkLevel {
  const t = transportToLevel(s.transportState);
  if (t === "critical") return "critical";
  if (t !== "good") return "muted";

  const hasLoss = Boolean(hasIntervalData && s.lossPercentRecent != null);
  const hasRtt = s.rttMs != null && Number.isFinite(Number(s.rttMs));

  if (!hasLoss && !hasRtt) return "good";

  let level: LinkLevel = "good";
  if (hasLoss) level = worseLevel(level, lossToLevel(s.lossPercentRecent));
  if (hasRtt) level = worseLevel(level, rttToLevel(s.rttMs));
  return level;
}
